import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';
import { CheckCircle, XCircle, Clock } from 'lucide-react';
import Header from './Header';
import './Dashboard.css';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const [redirecting, setRedirecting] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [cancelLoading, setCancelLoading] = useState(false);

  const fetchSubscription = async () => {
    try {
      setLoadingSubscription(true);
      const response = await api.get(`/api/stripe/subscription/${user.id}`);
      setSubscription(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
      setSubscription({ hasSubscription: false });
    } finally {
      setLoadingSubscription(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchSubscription();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchSubscription is stable, user.id is the trigger
  }, [user?.id]);

  const handleOpenIndyAI = () => {
    setRedirecting(true);
    const token = localStorage.getItem('accessToken');
    
    if (!token) {
      alert('No authentication token found. Please log in again.');
      setRedirecting(false);
      return;
    }
    
    // Use the proxy service to authenticate and redirect to Indy AI.
    // Always use chat subdomain in production (chat.portal.inlandfiber.com) so the full UI loads.
    // Ignore REACT_APP_LIBRECHAT_URL when it points at the portal path (/librechat) so deploy works
    // even if server .env has LIBRECHAT_PROXY_URL=https://portal.inlandfiber.com/librechat.
    const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
    const host = window.location.hostname;
    const protocol = window.location.protocol;
    let base;
    if (isProduction) {
      const envUrl = (process.env.REACT_APP_LIBRECHAT_URL || '').replace(/\/$/, '');
      const envHost = envUrl.startsWith('http') ? (() => { try { return new URL(envUrl).hostname; } catch { return ''; } })() : '';
      // Only use env URL if it's already the chat subdomain; otherwise force chat subdomain
      if (envHost && envHost.startsWith('chat.') && envHost === `chat.${host}`) {
        base = envUrl;
      } else {
        base = `${protocol}//chat.${host}`;
      }
    } else {
      base = (process.env.REACT_APP_LIBRECHAT_URL || 'http://localhost:5001').replace(/\/$/, '');
    }
    const baseHost = (() => { try { return new URL(base).hostname; } catch { return ''; } })();
    const useSubdomain = baseHost && baseHost !== host;
    const authPath = (base.includes('localhost') || base.includes('127.0.0.1') || base.endsWith('/librechat') || useSubdomain)
      ? '/auth'
      : '/librechat/auth';
    const authUrl = `${base}${authPath}?token=${token}`;
    
    // Open in new window
    window.open(authUrl, '_blank');
    
    setTimeout(() => setRedirecting(false), 1000);
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Cancel your subscription? You will keep access until the end of your current billing period.')) {
      return;
    }
    setCancelLoading(true);
    try {
      await api.post('/api/stripe/cancel-subscription');
      toast.success('Subscription will cancel at the end of your billing period.');
      await fetchSubscription();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to cancel subscription';
      toast.error(msg);
    } finally {
      setCancelLoading(false);
    }
  };

  // Map Stripe product name to a friendly subscription type for display
  const getSubscriptionPlanLabel = (productName) => {
    if (!productName) return 'Subscription';
    const n = productName.toLowerCase();
    if (n.includes('indy ai')) return 'Indy AI (Chat)';
    if (n.includes('colo')) return 'Colo by the U';
    return productName;
  };

  return (
    <div>
      <Header />
      <div className="dashboard-container">
        <div className="dashboard-card">
          <div className="dashboard-header">
            <h1>Welcome, {user?.username || 'User'}!</h1>
            <button onClick={handleLogout} className="logout-button">
              Logout
            </button>
          </div>

          <div className="user-info">
            <div className="info-item">
              <span className="info-label">Email:</span>
              <span className="info-value">{user?.email}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Username:</span>
              <span className="info-value">{user?.username}</span>
            </div>
            {user?.fullName && (
              <div className="info-item">
                <span className="info-label">Full Name:</span>
                <span className="info-value">{user.fullName}</span>
              </div>
            )}
          </div>

          {/* Subscription Information */}
          <div className="subscription-section">
            <h2>Subscriptions</h2>
            {loadingSubscription ? (
              <p className="subscription-loading">Loading subscription information...</p>
            ) : subscription?.hasSubscription && subscription?.subscription ? (
              <div className="subscription-info">
                <div className="subscription-plan-header">
                  <span className="subscription-plan-label">
                    {getSubscriptionPlanLabel(subscription.subscription.product?.name)}
                  </span>
                  {subscription.subscription.status === 'active' ? (
                    <div className="status-badge status-active">
                      <CheckCircle className="status-icon" />
                      <span>Active</span>
                    </div>
                  ) : subscription.subscription.status === 'canceled' ? (
                    <div className="status-badge status-canceled">
                      <XCircle className="status-icon" />
                      <span>Canceled</span>
                    </div>
                  ) : (
                    <div className="status-badge status-pending">
                      <Clock className="status-icon" />
                      <span>{subscription.subscription.status}</span>
                    </div>
                  )}
                </div>

                <div className="subscription-details">
                  <div className="subscription-detail-item">
                    <span className="detail-label">Price:</span>
                    <span className="detail-value">
                      ${(subscription.subscription.price.amount / 100).toFixed(2)} / {subscription.subscription.price.interval}
                    </span>
                  </div>
                  {subscription.subscription.current_period_end && (
                    <div className="subscription-detail-item">
                      <span className="detail-label">Renews:</span>
                      <span className="detail-value">
                        {new Date(subscription.subscription.current_period_end * 1000).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  {subscription.subscription.cancel_at_period_end && (
                    <div className="subscription-warning">
                      Your subscription will cancel at the end of the current billing period.
                    </div>
                  )}
                  {subscription.subscription.status === 'active' &&
                    !subscription.subscription.cancel_at_period_end && (
                      <div className="subscription-cancel-wrap">
                        <button
                          type="button"
                          onClick={handleCancelSubscription}
                          className="subscription-cancel-link"
                          disabled={cancelLoading}
                        >
                          {cancelLoading ? 'Cancelling…' : 'Cancel subscription'}
                        </button>
                      </div>
                    )}
                </div>
              </div>
            ) : (
              <div className="no-subscription">
                <p>You don't have an active subscription.</p>
                <a href="/pricing" className="subscribe-link">View Plans</a>
              </div>
            )}
          </div>

          <div className="dashboard-actions">
            <h2>Access Subscriptions:</h2>
            {(process.env.REACT_APP_INDY_AI_FREE_MODE === 'true' || (subscription?.hasSubscription && subscription?.subscription?.status === 'active')) ? (
              <>
                <p className="action-description">
                  {subscription?.hasSubscription && subscription?.subscription?.status === 'active'
                    ? 'You have an active subscription. Click below to open or view your subscriptions.'
                    : 'Click below to open or view your subscriptions.'}
                </p>
                <button
                  onClick={handleOpenIndyAI}
                  className="primary-button"
                  disabled={redirecting}
                >
                  {redirecting ? 'Opening Indy AI...' : 'Open Indy AI'}
                </button>
              </>
            ) : (
              <>
                <p className="action-description" style={{ color: '#94a3b8' }}>
                  A subscription is required to access Indy AI chat.
                </p>
                <a href="/pricing" className="subscribe-link" style={{ display: 'inline-block', marginTop: '0.5rem' }}>
                  Subscribe to Indy AI — {parseInt(process.env.REACT_APP_INDY_AI_PRICE_MONTHLY || '20', 10) === 0 ? 'Free' : `$${process.env.REACT_APP_INDY_AI_PRICE_MONTHLY || '20'}/month`}
                </a>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
